import React, { useEffect, useMemo, useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useApi } from '../../services/api';

type CandidateAccess = {
  liveKitUrl: string;
  participantIdentity: string;
  roomName: string;
  accessToken: string;
  expiresAt: string;
  permissions: {
    roomJoin: boolean;
    canPublish: boolean;
    canPublishSources: string[];
    canSubscribe: boolean;
    roomAdmin: boolean;
    roomCreate: boolean;
    roomRecord: boolean;
  };
};

type SessionStatus = {
  id: string;
  orchestrationState: string;
  room: { name: string | null; present: boolean; deletedAt: string | null };
  candidate: { joinedAt: string | null; cameraPublished: boolean; microphonePublished: boolean };
  worker: { dispatched: boolean; ready: boolean };
  avatar: { ready: boolean };
  recording: { status: string; objectPresent: boolean; deletedAt: string | null };
  terminal: { eventType: string | null };
  cleanup: { roomDeletedAt: string | null; recordingDeletedAt: string | null };
};

const AiInterviewOrchestrationSmokePanel: React.FC = () => {
  const session = useSession();
  const api = useApi();
  const isJason = session?.user?.email?.trim().toLowerCase() === 'jason@intellerview.com';
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [candidateAccess, setCandidateAccess] = useState<CandidateAccess | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [message, setMessage] = useState('Load the local enabled backend before creating a fictional session.');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isJason) return;
    void api.get('/ai-interviews/orchestration/config').then((response) => {
      setFlagEnabled(response.data?.result?.featureFlag?.enabled === true);
    }).catch(() => setFlagEnabled(false));
  }, [api, isJason]);

  const joinUrl = useMemo(() => {
    if (!candidateAccess) return null;
    const url = new URL('https://meet.livekit.io/custom');
    url.searchParams.set('liveKitUrl', candidateAccess.liveKitUrl);
    url.searchParams.set('token', candidateAccess.accessToken);
    return url.toString();
  }, [candidateAccess]);

  if (!isJason) return null;

  const run = async (label: string, action: () => Promise<void>) => {
    setBusy(true);
    setMessage(`${label}…`);
    try { await action(); }
    catch (error: any) {
      setMessage(`${label} failed (${error?.response?.data?.code || 'request_failed'}).`);
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    if (!status?.id) return;
    const response = await api.get(`/ai-interviews/orchestration/sessions/${status.id}`);
    setStatus(response.data.result);
  };

  const createSession = () => run(flagEnabled ? 'Creating fictional session' : 'Proving disabled session creation', async () => {
    const response = await api.post('/ai-interviews/orchestration/sessions', {
      candidateIdentity: `fictional-staging-${Date.now()}`,
      applicationRef: 'fictional-staging-liveavatar-proof',
    });
    setStatus(response.data.result.session);
    setCandidateAccess(response.data.result.candidateAccess);
    setMessage('Fictional session created. Join the embedded room with camera and microphone.');
  });

  const event = (eventType: string, extra: Record<string, unknown> = {}) => run(`Sending ${eventType}`, async () => {
    const response = await api.post(`/ai-interviews/orchestration/sessions/${status!.id}/events`, { eventType, ...extra });
    setStatus(response.data.result.session);
    setMessage(`${eventType} accepted for the exact session.`);
  });

  const action = (label: string, path: string) => run(label, async () => {
    const response = await api.post(`/ai-interviews/orchestration/sessions/${status!.id}/${path}`);
    setStatus(response.data.result);
    setMessage(`${label} completed for the exact session.`);
  });

  const requestPlayback = () => run('Requesting private playback', async () => {
    const response = await api.post(`/ai-interviews/orchestration/sessions/${status!.id}/playback`);
    setPlaybackUrl(response.data.result.signedUrl);
    setMessage('Private short-lived playback is ready in this browser only.');
  });

  const deleteRecording = () => run('Deleting exact recording', async () => {
    const response = await api.delete(`/ai-interviews/orchestration/sessions/${status!.id}/recording`);
    setStatus(response.data.result);
    setPlaybackUrl(null);
    setMessage('Exact private recording deleted.');
  });

  const deleteSession = () => run('Deleting exact durable session', async () => {
    await api.delete(`/ai-interviews/orchestration/sessions/${status!.id}`);
    setStatus(null);
    setCandidateAccess(null);
    setPlaybackUrl(null);
    setMessage('Exact fictional durable session deleted.');
  });

  return (
    <section className="rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] px-5 py-4 shadow-sm" aria-label="AI interview orchestration smoke controls">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0369a1]">Jason-only local smoke</p>
          <h2 className="mt-1 text-[16px] font-bold text-[#0f172a]">Production orchestration controls</h2>
        </div>
        <span className="rounded-full border border-[#7dd3fc] bg-white px-2.5 py-1 text-[11px] font-bold text-[#075985]">
          Local flag {flagEnabled ? 'enabled' : 'disabled'}
        </span>
      </div>
      <p className="mt-3 text-[12px] text-[#475569]">{message}</p>

      {!status ? (
        <button type="button" disabled={busy} onClick={createSession}
          className="mt-4 rounded-[8px] border border-[#0284c7] bg-[#0284c7] px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50">
          {flagEnabled ? 'Create fictional orchestration session' : 'Prove session creation is disabled'}
        </button>
      ) : (
        <>
          <dl className="mt-4 grid gap-2 md:grid-cols-3 xl:grid-cols-6">
            {[
              ['Session', `…${status.id.slice(-8)}`],
              ['State', status.orchestrationState],
              ['Candidate tracks', status.candidate.cameraPublished && status.candidate.microphonePublished ? 'Camera + mic' : 'Not confirmed'],
              ['Worker / avatar', `${status.worker.ready ? 'Ready' : status.worker.dispatched ? 'Dispatched' : 'Idle'} / ${status.avatar.ready ? 'Ready' : 'Idle'}`],
              ['Recording', status.recording.status],
              ['Terminal', status.terminal.eventType || 'Not terminal'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-[6px] border border-[#bae6fd] bg-white px-3 py-2">
                <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#64748b]">{label}</dt>
                <dd className="mt-1 text-[12px] font-semibold text-[#334155]">{value}</dd>
              </div>
            ))}
          </dl>

          {joinUrl && !status.terminal.eventType ? (
            <div className="mt-4 rounded-[8px] border border-[#334155] bg-[#0f172a] p-3">
              <p className="mb-2 text-[12px] font-semibold text-white">Fictional candidate room</p>
              <iframe title="Fictional orchestration candidate room" src={joinUrl}
                allow="camera *; microphone *; fullscreen *; autoplay *" className="h-[420px] w-full rounded-[6px] border-0 bg-black" />
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => event('CANDIDATE_JOINED', { cameraPublished: true, microphonePublished: true })}>Confirm candidate camera + mic</button>
            <button type="button"
              disabled={busy || !status.candidate.cameraPublished || !status.candidate.microphonePublished || status.recording.objectPresent}
              onClick={() => action('Starting server recording', 'recording/start')}>Start server recording</button>
            <button type="button" disabled={busy || !status.recording.objectPresent || status.worker.dispatched}
              onClick={() => action('Dispatching worker', 'worker/dispatch')}>Dispatch worker</button>
            <button type="button" disabled={busy} onClick={() => run('Refreshing exact status', async () => { await refresh(); setMessage('Exact session status refreshed.'); })}>Refresh status</button>
            <button type="button" disabled={busy} onClick={() => action('Reconciling exact session', 'reconcile')}>Reconcile exact session</button>
            <button type="button" disabled={busy || !status.recording.objectPresent} onClick={requestPlayback}>Request private playback</button>
            <button type="button" disabled={busy || !status.recording.objectPresent} onClick={deleteRecording}>Delete exact recording</button>
            <button type="button" disabled={busy || !status.terminal.eventType} onClick={() => action('Cleaning exact room', 'cleanup')}>Clean exact room</button>
            <button type="button" disabled={busy || status.recording.objectPresent || !status.room.deletedAt} onClick={deleteSession}>Delete exact durable session</button>
          </div>
          {playbackUrl ? <video src={playbackUrl} controls playsInline className="mt-4 max-h-[420px] w-full rounded-[8px] bg-black" /> : null}
        </>
      )}
      <style>{`[aria-label="AI interview orchestration smoke controls"] button { border: 1px solid #0284c7; border-radius: 8px; background: white; padding: 8px 12px; color: #075985; font-size: 12px; font-weight: 600; } [aria-label="AI interview orchestration smoke controls"] button:disabled { opacity: .45; }`}</style>
    </section>
  );
};

export default AiInterviewOrchestrationSmokePanel;
